import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
import { logger, wrapKeyboardEvent } from '@salescore/frontend-common'
import { App } from 'antd'
import { Effect, pipe } from 'effect'
import { t } from 'i18next'
import { type KeyboardEvent, useCallback } from 'react'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import {
  copyMatrixClipboardJsonMimeType,
  copyMatrixClipboardWebJsonMimeType,
  copyMatrixToClipboardText,
  sheetAreaToCopyMatrix,
  toCopyMatrixClipboardPayload,
} from '../../../domain/copyMatrix'
import { useUpsertViewRecordsEntry } from '../../../hooks/useUpsertViewRecordsEntry'
import type { RSheetContext } from '../../../recoil/context'
import {
  useCursorMutation,
  useGetCursorSelector,
  useGetCursorSnapshot,
  useMoveToEdge,
  useSetMoveCursor,
} from '../../../recoil/cursor'
import { useRecordsImpl } from '../../../recoil/records'
import { useBackHistory, useForwardHistory } from '../../../recoil/recordsHistory'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { getReferencedStreamName } from '../../util/getReferencedStreamName'
import { getRecordNodeAttributeValue } from '../../util/innerRowRelated'
import { useClickCurrentCursorMutation } from './cursor/clickCurrentCursorMutation'
import { deleteCellValues } from './deleteCellValues'

export const useHandleKeyDownMutation = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  context: RSheetContext,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
  visibleRowNumber: number | undefined,
  // eslint-disable-next-line @typescript-eslint/max-params
): ((event: React.KeyboardEvent<HTMLDivElement>) => void) => {
  const rows = useRecordsImpl()
  const maxRowIndex = rows.length - 1
  const maxColumnIndex = columns.length - 1
  const { message } = App.useApp()
  const setClickCurrentCursor = useClickCurrentCursorMutation(
    context,
    columns,
    compiledConfig,
    ability,
    query,
    visibleRowNumber,
  )
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const setMoveToEdge = useMoveToEdge(columns, ability)
  const setMoveCursor = useSetMoveCursor(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    ability,
    visibleRowNumber,
  )
  const getCursorSelector = useGetCursorSelector(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    visibleRowNumber,
  )
  const setForwardHistory = useForwardHistory()
  const setBackHistory = useBackHistory()
  const getCursorSnapshot = useGetCursorSnapshot()
  const { onAfterCopyCell } = useCursorMutation(ability)
  const { selectAllCell, clearCursor } = useCursorMutation(ability)

  return useCallback(
    // eslint-disable-next-line complexity,sonarjs/cognitive-complexity -- キー入力別の分岐が多く、責務分割は別PRで対応する
    (event: KeyboardEvent<HTMLDivElement>) => {
      const cursor = getCursorSnapshot()
      const data = rows
      if (cursor?.editing?.isEditing === true) {
        return
      }
      const wrapedEvent = wrapKeyboardEvent(event)
      logger.debug(`handleKeyDownMutation`, event.key, wrapedEvent) // 2022/05 windowsでの不具合対応
      const column = cursor?.main.columnIndex === undefined ? undefined : columns[cursor.main.columnIndex]

      const moveCursorDefaultArgument = {
        columnDiff: 0,
        rowDiff: 0,
        data,
        columns,
        maxColumnIndex,
        maxRowIndex,
        expand: event.shiftKey,
      }

      switch (event.key) {
        case 'Enter': {
          if (column === undefined) {
            return
          }
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: 要仕様検討。書き込み禁止列は新規行追加できないようにするか？
          // 何にしろ、丁寧にやるなら新規行追加はしつつ、編集モードにしない対応が必要
          if (column.readonly === true) {
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
            void message.warning(t('この列は書き込み禁止です'))
            return
          }
          if (wrapedEvent.commandKey && cursor !== undefined) {
            if (column.nodeType === 'children' || column.nodeType === 'child') {
              // asyncで実装
              event.preventDefault()
              return
            }
            if (column.node.path.length <= 1) {
              // asyncで実装している
              event.preventDefault()
              return
            }
          } else {
            setClickCurrentCursor({})
          }
          event.preventDefault()
          return
        }
        case 'Escape': {
          clearCursor()
          event.preventDefault()
          return
        }
        case ' ': {
          // space
          // 基本的にエンターと同じ挙動をさせるが、一部異なる挙動をさせる
          const value = getRecordNodeAttributeValue(data, column, cursor?.main)
          if (column?.metaType === 'url' && typeof value === 'string') {
            window.open(value)
          } else if (column?.metaType === 'phone_number' && typeof value === 'string') {
            window.open(`tel:${value}`)
          } else if (column?.metaType === 'email' && typeof value === 'string') {
            window.open(`mailto:${value}`)
          } else {
            setClickCurrentCursor({})
          }
          event.preventDefault()
          return
        }
        case 'Backspace':
        case 'Delete': {
          if (cursor !== undefined) {
            const { selectedArea } = getCursorSelector(cursor)
            if (selectedArea === undefined) {
              return
            }
            const result = deleteCellValues(data, columns, cursor, selectedArea.area)
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
            result.errors.forEach((x) => {
              void message.warning(x.message)
            })
            upsertViewRecordsEntry({ recordChanges: result.recordChanges })

            event.preventDefault()
          }
          return
        }
        case 'ArrowLeft': {
          if (wrapedEvent.commandKey) {
            setMoveToEdge({ type: 'left' as const, expand: event.shiftKey })
          } else {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              columnDiff: -1,
            })
          }
          event.preventDefault()
          return
        }
        case 'ArrowRight': {
          if (wrapedEvent.commandKey) {
            setMoveToEdge({ type: 'right' as const, expand: event.shiftKey })
          } else {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              columnDiff: 1,
            })
          }
          event.preventDefault()
          return
        }
        case 'ArrowDown': {
          if (wrapedEvent.commandKey) {
            setMoveToEdge({ type: 'down' as const, expand: event.shiftKey })
          } else {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              rowDiff: 1,
            })
          }
          event.preventDefault()
          return
        }
        case 'ArrowUp': {
          if (wrapedEvent.commandKey) {
            setMoveToEdge({ type: 'up' as const, expand: event.shiftKey })
          } else {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              rowDiff: -1,
            })
          }
          event.preventDefault()
          return
        }
        case 'Tab': {
          if (event.shiftKey) {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              columnDiff: -1,
              expand: false, // Shift+Tabと競合しないよう、常にfalse
            })
          } else {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              columnDiff: 1,
              expand: false, // Shift+Tabと競合しないよう、常にfalse
            })
          }
          event.preventDefault()
          return
        }
        case 'c': {
          if (wrapedEvent.commandKey) {
            if (cursor === undefined) {
              return
            }
            const { selectedArea } = getCursorSelector(cursor)
            if (selectedArea === undefined) {
              return
            }
            const copyMatrix = sheetAreaToCopyMatrix(data, columns, selectedArea.area, (column) =>
              getReferencedStreamName(column, query?.tree),
            )
            const jsonBlob = new Blob([JSON.stringify(toCopyMatrixClipboardPayload(copyMatrix))], {
              type: copyMatrixClipboardJsonMimeType,
            })
            const textBlob = new Blob([copyMatrixToClipboardText(copyMatrix)], { type: 'text/plain' })
            const clipboardItem = new ClipboardItem({
              [copyMatrixClipboardWebJsonMimeType]: jsonBlob,
              [textBlob.type]: textBlob,
            })
            void pipe(
              Effect.tryPromise({
                try: async () => {
                  await navigator.clipboard.write([clipboardItem])
                  onAfterCopyCell()
                },
                catch: (error) => (error instanceof Error ? error : new Error('unknown error')),
              }),
              Effect.catch((error) => {
                logger.warn('SHEET_COPY', 'Failed to copy', { error: error.message })
                return Effect.void
              }),
              Effect.runPromise,
            )
            event.preventDefault()
            return
          }
          break
        }
        case 'z': {
          if (wrapedEvent.commandKey && wrapedEvent.shiftKey) {
            setForwardHistory()
            event.preventDefault()
            return
          }
          if (wrapedEvent.commandKey) {
            setBackHistory(1)
            event.preventDefault()
            return
          }
          break
        }
        case 'y': {
          if (wrapedEvent.commandKey) {
            setForwardHistory()
            event.preventDefault()
            return
          }
          break
        }
        case 's': {
          if (wrapedEvent.commandKey) {
            // eslint-disable-next-line sonarjs/todo-tag
            // void recordsAndChangesMutation.onSave() // TODO: async setはrecoilで未実装なため、mutationとして実装できない。ここだけuseAsyncHandleKeyDownで実装している。
            event.preventDefault()
            return
          }
          break
        }
        case 'a': {
          // 全てのセルを選択
          if (wrapedEvent.commandKey) {
            selectAllCell({ columns, rows })
            event.preventDefault()
            return
          }
          break
        }
      }

      if (wrapedEvent.macCtrlKey) {
        // emacs key binding
        switch (event.key) {
          case 'a': {
            setMoveToEdge({ type: 'left' as const, expand: event.shiftKey })
            event.preventDefault()
            return
          }
          case 'b': {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              columnDiff: -1,
            })
            event.preventDefault()
            return
          }
          case 'e': {
            setMoveToEdge({ type: 'right' as const, expand: event.shiftKey })
            event.preventDefault()
            return
          }
          case 'f': {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              columnDiff: 1,
            })
            event.preventDefault()
            return
          }
          case 'v': {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              rowDiff: maxRowIndex,
            })
            event.preventDefault()
            return
          }
          case 'n': {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              rowDiff: 1,
            })
            event.preventDefault()
            return
          }
          case 'p': {
            setMoveCursor({
              ...moveCursorDefaultArgument,
              rowDiff: -1,
            })
            event.preventDefault()
            return
          }
        }
      }

      if (column === undefined) {
        return
      }

      if (wrapedEvent.commandKey || event.shiftKey) {
        return
      }

      // 何らかの文字が入力されたとき、スプレッドシートのように入力開始させる
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: いったん実装したが、日本語入力に対応できていないことなどが微妙。おそらく、Input要素をhideしておくような形でないと実装できない
      if (event.key.length === 1 && ((event.key >= 'a' && event.key <= 'z') || event.key >= '0' || event.key <= '9')) {
        setClickCurrentCursor({ pressedKey: event.key })
        if (column.readonly === true) {
          return
        }
        event.preventDefault()
      }
    },
    [
      clearCursor,
      columns,
      getCursorSelector,
      getCursorSnapshot,
      maxColumnIndex,
      maxRowIndex,
      message,
      onAfterCopyCell,
      rows,
      selectAllCell,
      setBackHistory,
      setClickCurrentCursor,
      setForwardHistory,
      setMoveCursor,
      setMoveToEdge,
      upsertViewRecordsEntry,
    ],
  )
}
